import {
  isDataUrl,
  isPdfDataUrl,
  isPdfStoredUrl,
  isStoredFileUrl,
} from './imageAnswer';

/**
 * Guarda a pré-visualização local dos arquivos enviados por direct upload.
 *
 * O `signed_id` devolvido pela API não é uma URL exibível, então enquanto a
 * anotação não é salva a única forma de mostrar a miniatura é o object URL do
 * arquivo escolhido pelo usuário. O cache vive apenas na sessão do navegador.
 */

export type FilePreviewKind = 'image' | 'pdf' | 'unknown';

export interface UploadPreview {
  previewUrl: string;
  kind: 'image' | 'pdf';
  name: string;
}

export interface FilePreview {
  /** URL exibível (object URL, data URL ou URL do storage). */
  src?: string;
  kind: FilePreviewKind;
  name: string;
}

const cache = new Map<string, UploadPreview>();

export function setUploadPreview(signedId: string, preview: UploadPreview): void {
  cache.set(signedId, preview);
}

export function getUploadPreview(signedId: string): UploadPreview | undefined {
  return cache.get(signedId);
}

export function removeUploadPreview(signedId: string): void {
  const preview = cache.get(signedId);
  if (preview?.previewUrl.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(preview.previewUrl);
    } catch {
      /* ignore */
    }
  }
  cache.delete(signedId);
}

/**
 * Resolve como exibir um item do `value` de um campo de imagem:
 * URL do storage e data URL são exibidas direto; `signed_id` usa o cache local.
 */
export function resolveFilePreview(item: string): FilePreview {
  if (isStoredFileUrl(item)) {
    return { src: item, kind: isPdfStoredUrl(item) ? 'pdf' : 'image', name: '' };
  }

  if (isDataUrl(item)) {
    return { src: item, kind: isPdfDataUrl(item) ? 'pdf' : 'image', name: '' };
  }

  const cached = cache.get(item);
  if (cached) {
    return { src: cached.previewUrl, kind: cached.kind, name: cached.name };
  }

  return { kind: 'unknown', name: '' };
}
